import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { NavItem } from 'src/app/models/navigation.model';
import { NavigationService } from 'src/app/services/navigation.service';

@Component({
  selector: 'app-left-navigation',
  templateUrl: './left-navigation.component.html',
  styleUrls: ['./left-navigation.component.scss']
})
export class LeftNavigationComponent implements OnInit {
  navItems: NavItem[] = [];
  currentPath = '';
  activeNavItemId = '';

  constructor(
    private navigationService: NavigationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.navItems = this.navigationService.getNavItems();
    
    // Update the current path when navigation occurs and expand appropriate items
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      // Get the base URL without hash fragments
      let url = event.urlAfterRedirects;
      if (url.includes('#')) {
        url = url.split('#')[0];
      }

      this.currentPath = url;

      // Clear sub-item active state if we navigated away from that sub-item's page
      if (this.activeNavItemId) {
        const activeSubItem = this.findNavItemById(this.navItems, this.activeNavItemId);
        if (!activeSubItem || activeSubItem.path !== url) {
          this.activeNavItemId = '';
        }
      }

      this.expandRelevantItems();
      this.setActiveStateByUrl(url);
    });
    
    // Sync active sub-item when right nav (or any caller) scrolls to a section
    this.navigationService.getCurrentSectionId().subscribe(sectionId => {
      if (sectionId) {
        const subItem = this.findNavItemBySectionId(this.navItems, sectionId);
        if (subItem) {
          this.activeNavItemId = subItem.id;
        }
      }
    });

    // Set initial path and handle any hash fragments
    let initialUrl = this.router.url;
    if (initialUrl.includes('#')) {
      initialUrl = initialUrl.split('#')[0];
    }
    this.currentPath = initialUrl;
    this.expandRelevantItems();
    this.setActiveStateByUrl(initialUrl);
  }

  toggleItem(item: NavItem, event: Event): void {
    event.preventDefault(); // Prevent navigation for category toggle
    this.navigationService.toggleExpandItem(item);
    
    // If it's a category and we're expanding it, navigate to its first child
    if (item.isCategory && item.isExpanded) {
      const firstChildPath = this.navigationService.getFirstChildPath(item);
      if (firstChildPath) {
        this.router.navigateByUrl(firstChildPath);
      }
    }
  }

  onChevronClick(item: NavItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.navigationService.toggleExpandItem(item);
  }

  navigateTo(item: NavItem, event: Event): void {
    if (item.isSubItem) {
      event.preventDefault();
      this.activeNavItemId = item.id;
      if (item.sectionId) {
        if (this.currentPath === item.path) {
          this.navigationService.scrollToSection(item.sectionId);
        } else {
          this.router.navigateByUrl(item.path).then(() => {
            setTimeout(() => {
              //Timeout because we load the page info from json dynamically so divs aren't loaded by the time router completes
              //Could be attached to the page-content post load but this is easier faster and gets the job done
              this.navigationService.scrollToSection(item.sectionId!);
            }, 100)

          });
        }
      }
      return;
    }
    if (item.isCategory) {
      if (item.enableCategoryRouting) {
        event.preventDefault();
        this.navigationService.toggleExpandItem(item);
        this.router.navigateByUrl(item.path);
      } else {
        this.toggleItem(item, event);
      }
    }
  }

  isSubItemActive(item: NavItem): boolean {
    return item.id === this.activeNavItemId;
  }

  isActive(path: string): boolean {
    // Compare the path with current path, ignoring hash fragments
    if (this.currentPath.includes('#')) {
      return this.currentPath.split('#')[0] === path;
    }
    return this.currentPath === path;
  }

  // Helper method to set active state based on current URL
  private setActiveStateByUrl(url: string): void {
    // Find the menu item that matches this URL
    this.findAndMarkActiveItem(this.navItems, url);
  }

  // Recursive function to find and mark the active item
  private findAndMarkActiveItem(items: NavItem[], url: string): boolean {
    for (const item of items) {
      // Check if this item's path matches the URL
      if (!item.isCategory && item.path === url) {
        return true;
      }
      
      // Check children if they exist
      if (item.children && item.children.length > 0) {
        // For items with children, check if any child is active
        const childActive = this.findAndMarkActiveItem(item.children, url);
        if (childActive) {
          // Expand this item if a child is active
          item.isExpanded = true;
          return true;
        }
      }
    }
    return false;
  }

  private findNavItemBySectionId(items: NavItem[], sectionId: string): NavItem | null {
    for (const item of items) {
      if (item.isSubItem && item.sectionId === sectionId) return item;
      if (item.children?.length) {
        const found = this.findNavItemBySectionId(item.children, sectionId);
        if (found) return found;
      }
    }
    return null;
  }

  private findNavItemById(items: NavItem[], id: string): NavItem | null {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = this.findNavItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  private expandRelevantItems(): void {
    // Helper function to expand parent items based on current path
    const expandParents = (items: NavItem[], path: string): boolean => {
      for (const item of items) {
        if (item.path === path) {
          return true;
        }
        
        if (item.children && item.children.length > 0) {
          const childMatch = expandParents(item.children, path);
          if (childMatch) {
            item.isExpanded = true;
            return true;
          }
        }
      }
      return false;
    };
    
    expandParents(this.navItems, this.currentPath);
  }
}